import styled from '@emotion/styled'
import { Grid, Typography } from '@mui/material'
import parse, { domToReact, Element } from 'html-react-parser'
import React, { useState } from 'react'

import { Divider } from '@f-fertighaus/applications/Next/components/common/Divider.styled'
import { CatalogueIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/CatalogueIcon'
import { CheckIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/CheckIcon'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { isNotEmptyArray } from '@f-fertighaus/applications/Next/utils/typeguards/isNotEmptyArray'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { DetailsSectionWrapper } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/DetailsSection/common/DetailsSectionWrapper.styled'
import { WIZARD_REQUEST_TYPES } from '@f-fertighaus/applications/RequestWizard/partials/wizardConstants'
import { ClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'

import {
  ExposeDownloadButton,
  HouseDescriptionTextContainer
} from './HouseDescription.styled'

export const loadExposePrint = () =>
  import('@f-fertighaus/applications/Next/views/houseDetailPage/ExposePrint')
const ExposePrint = React.lazy(loadExposePrint)

export const HouseDescription = () => {
  const {
    state: { name, description, vendor_nid, features_verbose, active },
    featureFlags
  } = useHouseDetail()
  const [showExpose, setShowExpose] = useState(false)

  const handleLoadExpose = () => {
    loadExposePrint()
    setShowExpose(true)
  }

  const handleExposeDownloadClick = () => {
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'download-expose',
      indicates: ['funnel-entry'],
      location: 'details-section',
      values: [
        {
          type: 'text',
          value: t(
            'houseDetailPage.sections.details.description.ctaBanner.buttonText'
          )
        }
      ]
    })
  }

  const isDisabled = !active
  const showExposeSection = !featureFlags.includes('@FF_DISABLE_EXPOSE_SECTION')

  return (
    <DetailsSectionWrapper>
      <Typography
        component='h2'
        variant='body1'
        fontWeight='fontWeightBold'
        sx={{ marginBottom: 2, marginTop: 3 }}>
        {t(`houseDetailPage.sections.details.description.headline`, {
          houseName: name
        })}
      </Typography>
      <HouseDescriptionTextContainer typography='body1'>
        <ParsedDescription description={description} />
      </HouseDescriptionTextContainer>
      <Divider />
      <Grid container justifyContent='center'></Grid>
      {isNotEmptyArray(features_verbose) && (
        <>
          <Typography
            variant='body1'
            fontWeight='fontWeightBold'
            sx={{ marginBottom: 2, marginTop: 2 }}>
            {t(`houseDetailPage.sections.details.overview.labels.features`)}
          </Typography>
          <FeaturesList>
            {features_verbose.map(feature => (
              <FeatureElement key={feature.id}>
                <CheckIcon />
                {feature.name}
              </FeatureElement>
            ))}
          </FeaturesList>
        </>
      )}
      {showExposeSection && (
        <>
          <Typography
            variant='body1'
            fontWeight='fontWeightBold'
            sx={{ marginBottom: 2, marginTop: 2 }}>
            {t(
              `houseDetailPage.sections.details.description.ctaBanner.headline`
            )}
          </Typography>
          <Typography variant='body1' sx={{ marginBottom: 2, marginTop: 2 }}>
            {t(
              `houseDetailPage.sections.details.description.ctaBanner.contentText`
            )}
          </Typography>
          <ExposeDownloadButton
            onMouseOver={handleLoadExpose}
            onFocus={handleLoadExpose}
            disabled={isDisabled}
            variant='contained'
            color='primary'
            className='js-request-button'
            data-request-type={WIZARD_REQUEST_TYPES.houseDetailExpose}
            data-funnel-variant='house-wizard-item-expose'
            {...(vendor_nid && { 'data-vendor-nid': vendor_nid })}
            onClick={handleExposeDownloadClick}
            startIcon={<CatalogueIcon />}>
            {t(
              'houseDetailPage.sections.details.description.ctaBanner.buttonText'
            )}
          </ExposeDownloadButton>
        </>
      )}
      <ClientOnly>
        <React.Suspense>{showExpose ? <ExposePrint /> : null}</React.Suspense>
      </ClientOnly>
    </DetailsSectionWrapper>
  )
}

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
`

const FeatureElement = styled.li`
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};

  svg {
    margin-right: ${({ theme }) => theme.spacing(1)};
  }
`

export const ParsedDescription = ({ description }: { description: string }) => {
  return (
    <>
      {parse(description, {
        trim: true,
        replace: domNode => {
          const element = (domNode as unknown) as Element
          if (
            element.type === 'tag' &&
            element.children &&
            element.name === 'p'
          ) {
            return (
              <p suppressHydrationWarning>{domToReact(element.children)}</p>
            )
          }
          return domNode
        }
      })}
    </>
  )
}
